import { useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconEdit, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Supplier } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

interface Props {
  opened: boolean;
  onClose: () => void;
  onChange?: () => void;
}

interface EditState {
  id: number;
  name: string;
  url: string;
  contact: string;
}

export function SuppliersModal({ opened, onClose, onChange }: Props) {
  const { t } = useTranslation();
  const [list, setList] = useState<Supplier[]>([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  // Inline-edit row state — null = no row is being edited
  const [editing, setEditing] = useState<EditState | null>(null);

  async function reload() {
    try {
      setList(await api.listSuppliers());
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  useEffect(() => {
    if (opened) void reload();
  }, [opened]);

  async function add() {
    if (!name.trim()) return;
    try {
      await api.createSupplier({ name: name.trim(), url: url.trim() || null });
      setName("");
      setUrl("");
      await reload();
      onChange?.();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function toggleActive(s: Supplier) {
    try {
      await api.updateSupplier(s.id, { active: !s.active });
      await reload();
      onChange?.();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function remove(s: Supplier) {
    if (!confirm(t("procurement.confirm.deleteSupplier", { name: s.name }))) return;
    try {
      await api.deleteSupplier(s.id);
      await reload();
      onChange?.();
    } catch (e) {
      let msg = e instanceof ApiError ? e.message : String(e);
      if (e instanceof ApiError && (e.detail as { error?: string; orders_count?: number } | null)?.error === "supplier_in_use") {
        const n = (e.detail as { orders_count?: number } | null)?.orders_count ?? 0;
        msg = t("procurement.supplier.inUse", { count: n });
      }
      notifications.show({ color: "red", message: msg });
    }
  }

  function startEdit(s: Supplier) {
    setEditing({ id: s.id, name: s.name, url: s.url ?? "", contact: s.contact ?? "" });
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!editing) return;
    const trimmedName = editing.name.trim();
    if (!trimmedName) {
      notifications.show({ color: "red", message: t("procurement.supplier.nameRequired") });
      return;
    }
    try {
      await api.updateSupplier(editing.id, {
        name: trimmedName,
        url: editing.url.trim() || null,
        contact: editing.contact.trim() || null,
      });
      setEditing(null);
      await reload();
      onChange?.();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("procurement.manageSuppliers")} size="lg" centered>
      <Stack>
        <Group align="end">
          <TextInput
            label={t("procurement.supplier.name")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <TextInput
            label={t("procurement.supplier.url")}
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button leftSection={<IconPlus size={14} />} onClick={() => void add()}>
            {t("procurement.supplier.add")}
          </Button>
        </Group>

        <ResponsiveTable
          mobile={{
            rows: list,
            rowKey: (s) => s.id,
            title: (s) => s.name,
            alwaysOpen: true,
            fields: [
              {
                label: t("procurement.supplier.name"),
                render: (s) =>
                  editing?.id === s.id ? (
                    <TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.currentTarget.value })} size="xs" />
                  ) : null,
                hidden: (s) => editing?.id !== s.id,
              },
              {
                label: t("procurement.supplier.url"),
                render: (s) =>
                  editing?.id === s.id ? (
                    <TextInput value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.currentTarget.value })} size="xs" />
                  ) : (
                    s.url ?? "—"
                  ),
              },
              {
                label: t("procurement.supplier.contact"),
                render: (s) =>
                  editing?.id === s.id ? (
                    <TextInput value={editing.contact} onChange={(e) => setEditing({ ...editing, contact: e.currentTarget.value })} size="xs" />
                  ) : (
                    s.contact ?? "—"
                  ),
              },
              {
                label: t("procurement.supplier.active"),
                render: (s) => <Switch checked={s.active} onChange={() => void toggleActive(s)} disabled={editing?.id === s.id} />,
              },
            ],
            actions: (s) => (
              <Group gap="xs" wrap="nowrap" justify="flex-end">
                {editing?.id === s.id ? (
                  <>
                    <ActionIcon size="lg" color="teal" variant="subtle" onClick={() => void saveEdit()} title={t("common.save")}>
                      <IconCheck size={16} />
                    </ActionIcon>
                    <ActionIcon size="lg" variant="subtle" onClick={cancelEdit} title={t("common.cancel")}>
                      <IconX size={16} />
                    </ActionIcon>
                  </>
                ) : (
                  <>
                    <ActionIcon size="lg" variant="subtle" onClick={() => startEdit(s)} title={t("common.edit")}>
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon size="lg" color="red" variant="subtle" onClick={() => void remove(s)} title={t("common.delete")}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </>
                )}
              </Group>
            ),
          }}
        >
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("procurement.supplier.name")}</Table.Th>
              <Table.Th>{t("procurement.supplier.url")}</Table.Th>
              <Table.Th>{t("procurement.supplier.contact")}</Table.Th>
              <Table.Th>{t("procurement.supplier.active")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.map((s) => {
              const isEditing = editing?.id === s.id;
              return (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    {isEditing ? (
                      <TextInput
                        value={editing!.name}
                        onChange={(e) => setEditing({ ...editing!, name: e.currentTarget.value })}
                        size="xs"
                      />
                    ) : (
                      s.name
                    )}
                  </Table.Td>
                  <Table.Td>
                    {isEditing ? (
                      <TextInput
                        value={editing!.url}
                        onChange={(e) => setEditing({ ...editing!, url: e.currentTarget.value })}
                        size="xs"
                      />
                    ) : (
                      s.url ?? "—"
                    )}
                  </Table.Td>
                  <Table.Td>
                    {isEditing ? (
                      <TextInput
                        value={editing!.contact}
                        onChange={(e) => setEditing({ ...editing!, contact: e.currentTarget.value })}
                        size="xs"
                      />
                    ) : (
                      s.contact ?? "—"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Switch checked={s.active} onChange={() => void toggleActive(s)} disabled={isEditing} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap" justify="flex-end">
                      {isEditing ? (
                        <>
                          <ActionIcon color="teal" variant="subtle" onClick={() => void saveEdit()} title={t("common.save")}>
                            <IconCheck size={14} />
                          </ActionIcon>
                          <ActionIcon variant="subtle" onClick={cancelEdit} title={t("common.cancel")}>
                            <IconX size={14} />
                          </ActionIcon>
                        </>
                      ) : (
                        <>
                          <ActionIcon variant="subtle" onClick={() => startEdit(s)} title={t("common.edit")}>
                            <IconEdit size={14} />
                          </ActionIcon>
                          <ActionIcon color="red" variant="subtle" onClick={() => void remove(s)} title={t("common.delete")}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </ResponsiveTable>
      </Stack>
    </Modal>
  );
}
